import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import type { Control, EntityState, GuestAction } from '@guest/shared';
import { Icon } from '../lib/icons.js';
import { useHouse } from '../lib/store.js';
import { Segmented, Slider, Toggle } from './ui.js';
import {
  brightnessPct,
  climateInfo,
  groupMembers,
  PRESET_LABELS,
  formatSensor,
  HVAC_LABELS,
  isOn,
  isUnavailable,
  mediaSummary,
  num,
  pctToBrightness,
  str,
  supportsBrightness,
  supportsColorTemp,
} from '../lib/entity.js';

interface Props {
  control: Control;
  state: EntityState | undefined;
}

function useAct(): {
  act: (action: GuestAction) => void;
  isPending: (entityId: string) => boolean;
} {
  const { act, pending } = useHouse();
  return {
    act: (action) => {
      void act(action).catch(() => undefined);
    },
    isPending: (entityId) => pending.has(entityId),
  };
}

function Head({
  control,
  on,
  sub,
  right,
}: {
  control: Control;
  on: boolean;
  sub?: string;
  right?: ReactNode;
}): ReactNode {
  return (
    <div className="ctrl-head">
      <span className="ctrl-icon" data-on={on}>
        <Icon name={control.icon ?? defaultIcon(control)} size={18} />
      </span>
      <span className="ctrl-text">
        <span className="ctrl-name">{control.name}</span>
        {sub && <span className="ctrl-sub">{sub}</span>}
      </span>
      {right}
    </div>
  );
}

function defaultIcon(control: Control): string {
  switch (control.kind) {
    case 'light':
      return 'bulb';
    case 'climate':
      return 'radiator';
    case 'cover':
      return 'blind';
    case 'media':
      return 'music';
    case 'camera':
      return 'camera';
    case 'binary_sensor':
      return 'door';
    case 'sensor':
      return 'thermometer';
    default:
      return 'power';
  }
}

// ------------------------------------------------------------------- light

function LightControl({ control, state }: Props): ReactNode {
  const { act, isPending } = useAct();
  const on = isOn(state);
  const pct = brightnessPct(state);
  const dimmable = supportsBrightness(state);
  const unavailable = isUnavailable(state);

  return (
    <div className="ctrl" data-unavailable={unavailable}>
      <Head
        control={control}
        on={on}
        sub={unavailable ? 'Unavailable' : on ? (dimmable ? `${pct}%` : 'On') : 'Off'}
        right={
          <Toggle
            on={on}
            busy={isPending(control.entityId)}
            disabled={unavailable}
            label={`${control.name} on or off`}
            onChange={() => act({ action: 'toggle', entityId: control.entityId })}
          />
        }
      />
      {on && dimmable && !unavailable && (
        <div className="ctrl-body">
          <Slider
            value={pct}
            min={1}
            max={100}
            label="Brightness"
            format={(v) => `${Math.round(v)}%`}
            onCommit={(v) =>
              act({ action: 'set_brightness', entityId: control.entityId, brightness: pctToBrightness(v) })
            }
          />
          {supportsColorTemp(state) && (
            <Slider
              value={num(state?.attributes['color_temp_kelvin']) ?? 3000}
              min={2200}
              max={6500}
              step={100}
              label="Warmth"
              format={(v) => `${Math.round(v)}K`}
              onCommit={(v) =>
                act({ action: 'set_color_temp', entityId: control.entityId, kelvin: Math.round(v) })
              }
            />
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ switch

function SwitchControl({ control, state }: Props): ReactNode {
  const { act, isPending } = useAct();
  const on = isOn(state);
  const unavailable = isUnavailable(state);
  return (
    <div className="ctrl" data-unavailable={unavailable}>
      <Head
        control={control}
        on={on}
        sub={unavailable ? 'Unavailable' : on ? 'On' : 'Off'}
        right={
          <Toggle
            on={on}
            busy={isPending(control.entityId)}
            disabled={unavailable}
            label={`${control.name} on or off`}
            onChange={() => act({ action: 'toggle', entityId: control.entityId })}
          />
        }
      />
    </div>
  );
}

// ----------------------------------------------------------------- climate

function ClimateControl({ control, state }: Props): ReactNode {
  const { act } = useAct();
  const info = climateInfo(state);
  const unavailable = isUnavailable(state);
  const mode = state?.state ?? 'off';
  const running = mode !== 'off' && mode !== 'unavailable';

  // Optimistic setpoint: the thermostat can take several seconds to echo back,
  // and a stepper that doesn't move on tap feels broken.
  const [draft, setDraft] = useState<number | null>(null);
  // A stat in standby reports no target at all; fall back so the guest can
  // still dial one in rather than seeing an empty card.
  const target = draft ?? info.target ?? (info.supportsTarget ? info.fallbackTarget : undefined);
  const unset = info.target === undefined && info.supportsTarget;

  const nudge = (delta: number): void => {
    if (target === undefined) return;
    const next = Math.min(info.max, Math.max(info.min, Math.round((target + delta) * 2) / 2));
    setDraft(next);
    act({ action: 'set_temperature', entityId: control.entityId, temperature: next });
    window.setTimeout(() => setDraft(null), 4000);
  };

  return (
    <div className="ctrl" data-unavailable={unavailable}>
      <Head
        control={control}
        on={running}
        sub={info.action ? info.action.replace(/_/g, ' ') : HVAC_LABELS[mode] ?? mode}
        right={
          info.current !== undefined ? (
            <span className="climate-now">{info.current.toFixed(1)}°</span>
          ) : undefined
        }
      />
      <div className="ctrl-body">
        {target !== undefined && (
          <div className="stepper">
            <button type="button" aria-label="Lower target temperature" onClick={() => nudge(-0.5)}>
              −
            </button>
            <span className="stepper-value">{target.toFixed(1)}°</span>
            <button type="button" aria-label="Raise target temperature" onClick={() => nudge(0.5)}>
              +
            </button>
            <span className="ctrl-sub" style={{ marginLeft: 4 }}>
              {unset ? 'not set — tap to set' : 'target'}
            </span>
          </div>
        )}
        {info.supportsPreset && info.presets.length > 1 && (
          <Segmented
            label={`${control.name} mode`}
            value={info.preset ?? null}
            options={info.presets.map((p) => ({ value: p, label: PRESET_LABELS[p] ?? p }))}
            onChange={(p) => act({ action: 'set_preset', entityId: control.entityId, preset: p })}
          />
        )}
        {!info.singleMode && (
          <Segmented
            label={`${control.name} heating mode`}
            value={mode}
            options={info.modes.map((m) => ({ value: m, label: HVAC_LABELS[m] ?? m }))}
            onChange={(m) => act({ action: 'set_hvac_mode', entityId: control.entityId, mode: m })}
          />
        )}
        {info.fanModes.length > 1 && running && (
          <Segmented
            label={`${control.name} fan`}
            value={str(state?.attributes['fan_mode']) ?? null}
            options={info.fanModes.map((m) => ({ value: m, label: m }))}
            onChange={(m) => act({ action: 'set_fan_mode', entityId: control.entityId, mode: m })}
          />
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------- cover

function CoverControl({ control, state }: Props): ReactNode {
  const { act } = useAct();
  const position = num(state?.attributes['current_position']);
  const unavailable = isUnavailable(state);
  const open = state?.state === 'open' || (position ?? 0) > 0;

  return (
    <div className="ctrl" data-unavailable={unavailable}>
      <Head
        control={control}
        on={open}
        sub={position !== undefined ? `${Math.round(position)}% open` : (state?.state ?? '—')}
        right={
          <div className="transport">
            <button
              type="button"
              className="icon-btn"
              aria-label={`Open ${control.name}`}
              onClick={() => act({ action: 'cover_open', entityId: control.entityId })}
            >
              <Icon name="chevron" size={16} style={{ transform: 'rotate(-90deg)' }} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={`Stop ${control.name}`}
              onClick={() => act({ action: 'cover_stop', entityId: control.entityId })}
            >
              <Icon name="close" size={13} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={`Close ${control.name}`}
              onClick={() => act({ action: 'cover_close', entityId: control.entityId })}
            >
              <Icon name="chevron" size={16} style={{ transform: 'rotate(90deg)' }} />
            </button>
          </div>
        }
      />
      {position !== undefined && !unavailable && (
        <div className="ctrl-body">
          <Slider
            value={position}
            label="Position"
            format={(v) => `${Math.round(v)}%`}
            onCommit={(v) =>
              act({ action: 'cover_position', entityId: control.entityId, position: Math.round(v) })
            }
          />
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------- media

function MediaControl({ control, state }: Props): ReactNode {
  const { act } = useAct();
  const { context, states } = useHouse();
  const [grouping, setGrouping] = useState(false);
  const unavailable = isUnavailable(state);
  const playing = state?.state === 'playing';
  const art = str(state?.attributes['entity_picture']);
  const volume = num(state?.attributes['volume_level']);

  // Every other speaker this guest can reach, across all their rooms — grouping
  // is inherently cross-room, so it cannot be scoped to the current one.
  // Label with room *and* zone: a guest room's bedroom and ensuite speakers are
  // both called "Sonos", so the room name alone cannot tell them apart.
  const others = (context?.rooms ?? [])
    .flatMap((r) =>
      r.controls
        .filter((c) => c.kind === 'media')
        .map((c) => ({
          label: [r.name, c.zone, c.name === 'Sonos' ? null : c.name].filter(Boolean).join(' · '),
          c,
        })),
    )
    .filter(({ c }) => c.entityId !== control.entityId);
  const joined = groupMembers(state, control.entityId);

  const toggleMember = (entityId: string): void => {
    if (joined.includes(entityId)) {
      // Unjoin is issued against the speaker leaving, not the group leader.
      act({ action: 'media_unjoin', entityId });
    } else {
      act({ action: 'media_join', entityId: control.entityId, members: [entityId] });
    }
  };

  return (
    <div className="ctrl" data-unavailable={unavailable}>
      <div className="ctrl-head">
        <span className="media-art">
          {art ? <img src={art} alt="" /> : <Icon name="music" size={20} />}
        </span>
        <span className="ctrl-text">
          <span className="ctrl-name">{control.name}</span>
          <span className="ctrl-sub">
            {mediaSummary(state)}
            {joined.length > 0 && ` · grouped with ${joined.length}`}
          </span>
        </span>
      </div>
      {!unavailable && (
        <div className="ctrl-body">
          <div className="transport">
            <button
              type="button"
              className="icon-btn"
              aria-label="Previous track"
              onClick={() => act({ action: 'media_previous', entityId: control.entityId })}
            >
              <Icon name="prev" size={16} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label={playing ? 'Pause' : 'Play'}
              onClick={() => act({ action: 'media_play_pause', entityId: control.entityId })}
            >
              <Icon name={playing ? 'pause' : 'play'} size={15} />
            </button>
            <button
              type="button"
              className="icon-btn"
              aria-label="Next track"
              onClick={() => act({ action: 'media_next', entityId: control.entityId })}
            >
              <Icon name="next" size={16} />
            </button>
            {others.length > 0 && (
              <button
                type="button"
                className="chip"
                aria-expanded={grouping}
                style={{ marginLeft: 'auto' }}
                onClick={() => setGrouping((g) => !g)}
              >
                <Icon name="link" size={14} />
                {joined.length > 0 ? `Grouped (${joined.length})` : 'Group'}
              </button>
            )}
          </div>

          {grouping && (
            <div className="group-list">
              <div className="ctrl-sub" style={{ marginBottom: 6 }}>
                Play {control.name} in these rooms too
              </div>
              {others.map(({ label, c }) => {
                const on = joined.includes(c.entityId);
                const off = isUnavailable(states.get(c.entityId));
                return (
                  <button
                    key={c.entityId}
                    type="button"
                    className="group-row"
                    data-on={on}
                    disabled={off}
                    onClick={() => toggleMember(c.entityId)}
                  >
                    <span className="tick">
                      <Icon name="check" size={10} strokeWidth={3} />
                    </span>
                    <span className="nm">{label}</span>
                    {off && <span className="ctrl-sub">offline</span>}
                  </button>
                );
              })}
            </div>
          )}

          {volume !== undefined && (
            <Slider
              value={Math.round(volume * 100)}
              label="Volume"
              format={(v) => `${Math.round(v)}%`}
              onCommit={(v) => act({ action: 'media_volume', entityId: control.entityId, level: v / 100 })}
            />
          )}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ camera

/**
 * Full-screen live view, portalled into `.app` — the fixed, full-viewport box
 * the guest app lives in — rather than into <body>. A fixed overlay appended to
 * <body> while `.app` is itself fixed does not get repainted by WKWebView until
 * something else dirties the layer, so on iOS the tap appeared to do nothing.
 * Staying inside `.app`'s layer tree avoids that entirely.
 */
function CameraViewer({
  control,
  onClose,
}: {
  control: Control;
  onClose: () => void;
}): ReactNode {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    // Closing with the phone's back gesture is what a guest will try first.
    history.pushState({ camera: control.entityId }, '');
    window.addEventListener('popstate', onClose);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('popstate', onClose);
    };
  }, [onClose, control.entityId]);

  const close = (): void => {
    // Unwind the history entry pushed above unless the back gesture already did.
    if (history.state?.camera === control.entityId) history.back();
    else onClose();
  };

  return createPortal(
    <div className="camera-viewer" role="dialog" aria-modal="true" aria-label={control.name}>
      <button type="button" className="camera-viewer-scrim" aria-label="Close" onClick={close} />
      <div className="camera-viewer-inner">
        <header>
          <span className="live-dot" />
          <h2>{control.name}</h2>
          <span className="spacer" />
          <button type="button" className="icon-btn" aria-label="Close" onClick={close}>
            <Icon name="close" size={16} />
          </button>
        </header>
        {failed ? (
          <div className="camera-fallback" style={{ minHeight: 220 }}>
            <Icon name="camera" size={22} />
            <span>Camera unavailable</span>
          </div>
        ) : (
          <img
            src={`/api/guest/camera/${encodeURIComponent(control.entityId)}/stream`}
            alt={control.name}
            onError={() => setFailed(true)}
          />
        )}
      </div>
    </div>,
    document.querySelector('.app') ?? document.body,
  );
}

/**
 * Frames come through our own server, which adds the Home Assistant token — the
 * browser never holds one. Tiles poll a still every 15s while actually on
 * screen; tapping opens the full-screen live view above.
 */
export function CameraTile({ control }: { control: Control }): ReactNode {
  const [open, setOpen] = useState(false);
  const [tick, setTick] = useState(() => Date.now());
  const [failed, setFailed] = useState(false);
  const [onScreen, setOnScreen] = useState(false);
  const ref = useRef<HTMLElement>(null);

  // Frames are full 1080p (~250kB each), so a tile that is scrolled out of view
  // must not keep pulling them down a guest's mobile data.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => setOnScreen(entry?.isIntersecting ?? false),
      { rootMargin: '100px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    // No point refreshing the thumbnail while the live view covers it.
    if (open || !onScreen) return;
    const refresh = (): void => {
      if (document.visibilityState === 'visible') setTick(Date.now());
    };
    refresh();
    const id = window.setInterval(refresh, 15_000);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [open, onScreen]);

  const src = `/api/guest/camera/${encodeURIComponent(control.entityId)}/snapshot?t=${tick}`;

  return (
    <>
      <figure className="camera-tile" ref={ref}>
        {failed ? (
          <div className="camera-fallback">
            <Icon name="camera" size={20} />
            <span>Unavailable</span>
          </div>
        ) : onScreen ? (
          <img
            src={src}
            alt={control.name}
            loading="lazy"
            decoding="async"
            onError={() => setFailed(true)}
            onLoad={() => setFailed(false)}
          />
        ) : (
          <div className="skeleton" style={{ width: '100%', height: '100%' }} />
        )}
        <button
          type="button"
          className="camera-hit"
          aria-label={`Open ${control.name} live`}
          onClick={() => setOpen(true)}
        />
        <figcaption>{control.name}</figcaption>
      </figure>
      {open && <CameraViewer control={control} onClose={() => setOpen(false)} />}
    </>
  );
}

/**
 * A room's main light, as one on/off. Rooms can flag several controls as
 * primary (the four rows over the master bed, both outdoor floods); they move
 * together, and the switch reads "on" when any of them is.
 */
export function PrimaryToggle({
  controls,
  label,
}: {
  controls: Control[];
  label: string;
}): ReactNode {
  const { act, isPending } = useAct();
  const { states } = useHouse();
  if (controls.length === 0) return null;

  const anyOn = controls.some((c) => isOn(states.get(c.entityId)));
  const unavailable = controls.every((c) => isUnavailable(states.get(c.entityId)));

  return (
    <Toggle
      on={anyOn}
      disabled={unavailable}
      busy={controls.some((c) => isPending(c.entityId))}
      label={label}
      onChange={() => {
        for (const c of controls) {
          act({ action: anyOn ? 'turn_off' : 'turn_on', entityId: c.entityId });
        }
      }}
    />
  );
}

// ----------------------------------------------------------------- sensors

export function SensorGrid({
  controls,
  states,
}: {
  controls: Control[];
  states: ReadonlyMap<string, EntityState>;
}): ReactNode {
  return (
    <div className="sensor-grid">
      {controls.map((c) => {
        const s = states.get(c.entityId);
        const active = s?.state === 'on';
        return (
          <div key={c.entityId} className="sensor-tile">
            <div className="k">
              <Icon name={c.icon ?? (c.kind === 'binary_sensor' ? 'door' : 'thermometer')} size={12} />
              {c.name}
            </div>
            <div className="v" style={active ? { color: 'hsl(var(--caution))' } : undefined}>
              {formatSensor(s)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// -------------------------------------------------------------- dispatcher

export function ControlRow({ control, state }: Props): ReactNode {
  switch (control.kind) {
    case 'light':
      return <LightControl control={control} state={state} />;
    case 'climate':
      return <ClimateControl control={control} state={state} />;
    case 'cover':
      return <CoverControl control={control} state={state} />;
    case 'media':
      return <MediaControl control={control} state={state} />;
    case 'switch':
    case 'fan':
    case 'vacuum':
      return <SwitchControl control={control} state={state} />;
    default:
      return null;
  }
}
